import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Flex, Group} from '@mantine/core';
import type {Dispatch, SetStateAction} from 'react';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ANALYTICS_MODULE,
  CloseType,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import ExecutionFlowchartPane from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPane';
import type {ExecutionFlowchartReadinessSnapshot} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPaneTypes';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import ModalFooter from 'ui-library/modals/ModalFooter';

import type {ProcessUpdateFlowchartModalProps} from './ProcessUpdateFlowchartModal';

const useFlowchartUpdateModalStyles = createStyles((theme) => ({
  footerSection: {
    flexShrink: 0,
    margin: '0 !important',
    paddingTop: theme.spacing.md,
    paddingRight: theme.spacing.md,
    paddingBottom: theme.spacing.md,
  },
  rootColumn: {
    flex: 1,
    minHeight: 0,
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  innerColumn: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
}));

export type ImprovedProcessUpdateFlowchartModalProps = Omit<
  ProcessUpdateFlowchartModalProps,
  'onRequestClose' | 'onRequestSubmit'
> & {
  onClose: () => void;
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  readinessSnapshot: ExecutionFlowchartReadinessSnapshot;
};

/**
 * Update-flowchart modal layout: footer + {@link ExecutionFlowchartPane} (PCM mode) with draft
 * and readiness supplied by the parent modal container.
 */
export default function ImprovedProcessUpdateFlowchartModal(
  props: ImprovedProcessUpdateFlowchartModalProps
): JSX.Element {
  const {classes} = useFlowchartUpdateModalStyles();
  const {draftState, setDraftState, onClose, readinessSnapshot} = props;

  const handleClose = (closeType: CloseType): void => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: props.eventGroupId,
      action_origin: props.actionOrigin,
      action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
      close_type: closeType,
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });
    onClose();
  };

  const handleSubmit = (): void => {
    props.onFlowchartModified();
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: props.eventGroupId,
      action_origin: props.actionOrigin,
      action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });
    onClose();
  };

  return (
    <Flex direction="column" className={classes.rootColumn}>
      <Flex direction="column" className={classes.innerColumn}>
        <ExecutionFlowchartPane
          editorMode={FlowchartEditorMode.PCM_EDITOR}
          processId={props.flowchartProcessId}
          draftState={draftState}
          setDraftState={setDraftState}
          readinessSnapshot={readinessSnapshot}
        />
      </Flex>
      <ModalFooter sticky={false} className={classes.footerSection}>
        <Group position="right">
          <Button
            variant="secondary"
            onClick={() => {
              handleClose(CloseType.CANCEL);
            }}
            {...getTestProps({
              component: 'ImprovedProcessUpdateFlowchartModal',
              element: 'cancelButton',
            })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            {...getTestProps({
              component: 'ImprovedProcessUpdateFlowchartModal',
              element: 'submitButton',
            })}
          >
            Submit
          </Button>
        </Group>
      </ModalFooter>
    </Flex>
  );
}
